import { useCallback, useEffect, useRef, useState } from "react";
import {
  ACTIVITY_CHANNEL_NAME,
  ACTIVITY_CHECK_INTERVAL_MS,
  ACTIVITY_EVENTS,
  ACTIVITY_MESSAGE_TYPES,
  ACTIVITY_THROTTLE_MS,
  IDLE_WARNING_MESSAGE,
  THROTTLED_ACTIVITY_EVENTS,
  createActivityMessage,
  createTabId,
  createThrottle,
  getIdleAction,
  getInitialLastActivityTimestamp,
  hasAccessToken,
  reduceActivityChannelMessage,
} from "../utils/userActivity";

/**
 * Multi-tab idle detection for Layout.
 * Same behavior as the previous Layout inline logic, with throttled move events.
 *
 * @param {{ onIdleLogout: () => void }} options
 */
export default function useUserActivity({ onIdleLogout }) {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [activeTabsCount, setActiveTabsCount] = useState(1);

  const activityChannel = useRef(null);
  const tabId = useRef(createTabId());
  const lastActivityRef = useRef(getInitialLastActivityTimestamp());
  const showWarningRef = useRef(false);
  const activeTabsCountRef = useRef(1);
  const logoutRef = useRef(onIdleLogout);

  showWarningRef.current = showWarning;
  activeTabsCountRef.current = activeTabsCount;
  logoutRef.current = onIdleLogout;

  const clearWarning = useCallback(() => {
    if (!showWarningRef.current) return;
    setShowWarning(false);
    setSnackbarOpen(false);
  }, []);

  const recordActivity = useCallback(
    (options = {}) => {
      if (document.visibilityState !== "visible") return;

      const now = Date.now();
      lastActivityRef.current = now;

      if (!options.skipBroadcast) {
        activityChannel.current?.postMessage(
          createActivityMessage(
            ACTIVITY_MESSAGE_TYPES.USER_ACTIVITY,
            tabId.current,
            now
          )
        );
      }

      clearWarning();
    },
    [clearWarning]
  );

  // BroadcastChannel — keep tabs in sync for idle logout
  useEffect(() => {
    if (!hasAccessToken()) return undefined;

    if (!activityChannel.current) {
      activityChannel.current = new BroadcastChannel(ACTIVITY_CHANNEL_NAME);
      const channel = activityChannel.current;
      const initTimer = setTimeout(() => {
        channel.postMessage(
          createActivityMessage(
            ACTIVITY_MESSAGE_TYPES.TAB_INIT,
            tabId.current
          )
        );
      }, 100);

      const handleMessage = (event) => {
        const patch = reduceActivityChannelMessage(event.data, {
          currentTabId: tabId.current,
          lastActivityTimestamp: lastActivityRef.current,
        });
        if (!patch) return;

        if (patch.reply) {
          channel.postMessage(patch.reply);
        }
        if (patch.incrementTabs) {
          setActiveTabsCount((prev) => prev + 1);
        }
        if (patch.decrementTabs) {
          setActiveTabsCount((prev) => Math.max(0, prev - 1));
        }
        if (patch.lastActivityTimestamp != null) {
          lastActivityRef.current = patch.lastActivityTimestamp;
        }
        if (patch.clearWarning) {
          clearWarning();
        }
      };

      channel.addEventListener("message", handleMessage);

      const cleanup = () => {
        clearTimeout(initTimer);
        if (activityChannel.current) {
          activityChannel.current.postMessage(
            createActivityMessage(
              ACTIVITY_MESSAGE_TYPES.TAB_CLOSING,
              tabId.current
            )
          );
          activityChannel.current.removeEventListener("message", handleMessage);
          activityChannel.current.close();
          activityChannel.current = null;
        }
      };

      window.addEventListener("beforeunload", cleanup);

      return () => {
        window.removeEventListener("beforeunload", cleanup);
        cleanup();
      };
    }

    return undefined;
  }, [clearWarning]);

  // Idle check interval
  useEffect(() => {
    if (!hasAccessToken()) return undefined;

    const checkInactivity = setInterval(() => {
      if (!hasAccessToken()) return;
      if (activeTabsCountRef.current <= 0) return;

      const inactiveTime = Date.now() - lastActivityRef.current;
      const action = getIdleAction(inactiveTime, {
        warningShown: showWarningRef.current,
      });

      if (action === "logout") {
        logoutRef.current?.();
      } else if (action === "warn") {
        setWarningMessage(IDLE_WARNING_MESSAGE);
        setShowWarning(true);
        setSnackbarOpen(true);
      }
    }, ACTIVITY_CHECK_INTERVAL_MS);

    return () => clearInterval(checkInactivity);
  }, []);

  // DOM activity listeners (throttled for move events)
  useEffect(() => {
    if (!hasAccessToken()) return undefined;

    const handleActivity = () => {
      if (document.visibilityState === "visible") {
        recordActivity();
      }
    };

    const throttledActivity = createThrottle(
      handleActivity,
      ACTIVITY_THROTTLE_MS
    );

    ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, handleActivity);
    });
    THROTTLED_ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, throttledActivity);
    });
    document.addEventListener("visibilitychange", handleActivity);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, handleActivity);
      });
      THROTTLED_ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, throttledActivity);
      });
      document.removeEventListener("visibilitychange", handleActivity);
      throttledActivity.cancel?.();
    };
  }, [recordActivity]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  return {
    snackbarOpen,
    warningMessage,
    handleSnackbarClose,
    isLoggedIn: hasAccessToken(),
  };
}
